import lodashGet from "lodash/get";
import { type SocietyDashboardModuleValueConfig } from "shared/api/types/society/[societyId]/dashboards/[dashboardId]";

type DashboardSimpleModuleValueUtilProps = {
  /** What the module's data call returned: a count, or an aggregate such as `{ "_sum": { "total": "125.50" } }` */
  data: unknown;
  /** The module's value config; the first entry's `path` says where in an aggregate the number is */
  valueConfig?: SocietyDashboardModuleValueConfig[];
};

/** The aggregates a database returns, in the order a tile should prefer them */
const AGGREGATE_KEYS = ["_sum", "_avg", "_max", "_min", "_count"] as const;

const isPresent = (value: unknown): boolean =>
  value !== undefined && value !== null && typeof value !== "object";

/**
 * The text a count or sum tile shows. A `total` module returns a number and a
 * `sum` module returns the database's aggregate object, so a tile that printed
 * its data directly showed `[object Object]` for every sum. The number is found
 * at the value config's path when there is one, and otherwise as the first
 * aggregate's first value. Money comes back as a string and is shown as sent.
 */
export const dashboardSimpleModuleValueUtil = ({
  data,
  valueConfig,
}: DashboardSimpleModuleValueUtilProps): string => {
  if (data === undefined || data === null) {
    return "";
  }
  if (typeof data !== "object") {
    return String(data);
  }
  const path = valueConfig?.find((config) => config.path)?.path;
  if (path) {
    const configured = lodashGet(data, path);
    if (isPresent(configured)) {
      return String(configured);
    }
  }
  const aggregates = data as Record<string, unknown>;
  for (const key of AGGREGATE_KEYS) {
    const aggregate = aggregates[key];
    if (isPresent(aggregate)) {
      return String(aggregate);
    }
    if (aggregate && typeof aggregate === "object") {
      const first = Object.values(aggregate).find(isPresent);
      if (first !== undefined) {
        return String(first);
      }
    }
  }
  return "";
};
